import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useApi } from '../api-context.tsx'
import { notify } from '../feedback.ts'
import { platformKeys } from '../platform/keys.ts'
import { servicesKeys } from './useServices.ts'

/**
 * A plan's songs as one Spotify playlist, kept in ForHim's own Spotify
 * accounts and shared by its link (docs/15-MUSIC-AND-AI.md). Anyone who can
 * see the plan asks for it; its Editors refresh it. ForHim's accounts are
 * the platform operators' (`useSpotifyAccounts`): connecting one is OAuth —
 * Spotify sends them to id.forhim.io/spotify, which hands what it got to
 * `useSpotifyCallback` and goes back to the platform page.
 */
export const spotifyKeys = {
  all: () => [...servicesKeys.all(), 'spotify'] as const,
  plan: (planId: string) => [...spotifyKeys.all(), 'plan', planId] as const,
}

/** Can this plan have a playlist, and does it have one? */
export function useSpotifyPlaylist(planId: string, enabled = true) {
  const api = useApi()
  return useQuery({
    queryKey: spotifyKeys.plan(planId),
    enabled,
    queryFn: async () => {
      const { data, error } = await api.api.v1.spotify.plans({ planId }).get()
      if (error) throw error
      return data
    },
    // While someone's ask is making it, look again until it is there.
    refetchInterval: (query) => (query.state.data?.making ? 2000 : false),
  })
}

export type SpotifyPlanStatus = NonNullable<ReturnType<typeof useSpotifyPlaylist>['data']>
export type SpotifyPlaylist = NonNullable<SpotifyPlanStatus['playlist']>

/**
 * The plan's Spotify panel: whether to show the button at all, what the
 * panel says (make it, or the playlist), and its actions. `canRefresh` is
 * Editor on the plan's service type; `operator` sees why it is unavailable.
 */
export function useSpotifyPanel(
  planId: string,
  { canRefresh, operator }: { canRefresh: boolean; operator: boolean },
) {
  const api = useApi()
  const queryClient = useQueryClient()
  const status = useSpotifyPlaylist(planId)
  const key = spotifyKeys.plan(planId)
  const settle = (playlist: SpotifyPlaylist) =>
    queryClient.setQueryData<SpotifyPlanStatus>(key, (prev) =>
      prev
        ? { ...prev, available: true, playlist, making: false }
        : { available: true, unavailableReason: null, playlist, making: false },
    )
  const make = useMutation({
    mutationFn: async () => {
      const { data, error } = await api.api.v1.spotify.plans({ planId }).playlist.post()
      if (error) throw error
      return data
    },
    onSuccess: settle,
    onSettled: () => queryClient.invalidateQueries({ queryKey: key }),
  })
  const refresh = useMutation({
    mutationFn: async () => {
      const { data, error } = await api.api.v1.spotify.plans({ planId }).playlist.refresh.post()
      if (error) throw error
      return data
    },
    onSuccess: (playlist) => {
      settle(playlist)
      notify({ title: 'Playlist refreshed', tone: 'success' })
    },
  })
  const data = status.data
  const playlist = data?.playlist ?? null
  return {
    status,
    playlist,
    /** The header shows the button: there is (or can be) a playlist, or an operator should see why not. */
    showButton: Boolean(data && (data.available || operator)),
    /** Shown to operators only: why nobody can make one. */
    unavailable:
      data && !data.available
        ? data.unavailableReason === 'not-configured'
          ? 'Spotify isn’t set up on this server (the app’s client id and secret).'
          : 'No ForHim Spotify account has room. Connect one, or mark one active, in Admin › Spotify.'
        : null,
    /** Someone's ask is making it right now (the panel asks again shortly). */
    making: Boolean(data?.making) || make.isPending,
    canRefresh: canRefresh && playlist !== null,
    make,
    refresh,
    error: make.error ?? refresh.error ?? null,
  }
}

/** What Spotify sent back to /spotify, handed to the server once. */
export function useSpotifyCallback() {
  const api = useApi()
  return useMutation({
    mutationFn: async (body: { state: string; code?: string; error?: string }) => {
      const { data, error } = await api.api.v1.spotify.callback.post(body)
      if (error) throw error
      return data
    },
  })
}

/** ForHim's Spotify accounts (platform operators): how full each is, connect, mark full, disconnect. */
export function useSpotifyAccounts() {
  const api = useApi()
  const queryClient = useQueryClient()
  const key = platformKeys.adminSpotify()
  const accounts = useQuery({
    queryKey: key,
    queryFn: async () => {
      const { data, error } = await api.api.v1.admin.spotify.accounts.get()
      if (error) throw error
      return data
    },
  })
  const invalidate = () => queryClient.invalidateQueries({ queryKey: key })
  const connect = useMutation({
    mutationFn: async () => {
      const { data, error } = await api.api.v1.admin.spotify.connect.post()
      if (error) throw error
      return data.authorizeUrl
    },
  })
  const update = useMutation({
    mutationFn: async ({
      id,
      ...body
    }: {
      id: string
      status?: 'active' | 'full'
      capacity?: number
    }) => {
      const { data, error } = await api.api.v1.admin.spotify.accounts({ id }).patch(body)
      if (error) throw error
      return data
    },
    onSuccess: (account) => {
      notify({
        title: `Saved ${account.displayName ?? account.spotifyUserId}`,
        tone: 'success',
      })
      return invalidate()
    },
  })
  const disconnect = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await api.api.v1.admin.spotify.accounts({ id }).delete()
      if (error) throw error
    },
    onSuccess: () => {
      notify({ title: 'Disconnected the Spotify account', tone: 'success' })
      return invalidate()
    },
  })
  return { accounts, connect, update, disconnect }
}

export type SpotifyAccount = NonNullable<
  ReturnType<typeof useSpotifyAccounts>['accounts']['data']
>['accounts'][number]
